import { Cookies } from 'react-cookie';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils.js';

/**
 * Centralized Consent Management for All Tracking Providers
 * Reads consent from dataLayer and manages consent state across Meta, Google, TTD, Amazon, etc.
 */
const CONSENT_STORAGE_KEY = 'bc_tracking_consent';
const CONSENT_COOKIE_NAME = 'bc_t_c'; // bc_tracking_consent
let userConsent = null; // null = not set, true = granted, false = revoked
let consentListenerAttached = false;
let debug = false;
// Initialize cookies instance
const cookies = new Cookies();
// Registered tracking providers to notify of consent changes
const registeredProviders = [];
/**
 * Logging utilities
 */
const log = (...args) => {
    if (!debug)
        return;
    // eslint-disable-next-line no-console
    console.info(...['[consent-manager]'].concat(args));
};
const warn = (...args) => {
    if (!debug)
        return;
    // eslint-disable-next-line no-console
    console.warn(...['[consent-manager]'].concat(args));
};
/**
 * Load consent from cookie (primary) or localStorage (fallback)
 * Cookies allow cross-subdomain sharing (*.abc.com)
 */
function loadConsentFromStorage() {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        // First, try to read from cookie (cross-subdomain)
        const cookieValue = cookies.get(CONSENT_COOKIE_NAME);
        if (cookieValue !== undefined) {
            log('Loaded consent from cookie:', cookieValue);
            return cookieValue === 'true' || cookieValue === true;
        }
        // Fallback to localStorage for backwards compatibility
        if (typeof localStorage !== 'undefined') {
            const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
            if (stored !== null) {
                log('Loaded consent from localStorage (fallback):', stored);
                // If found in localStorage, migrate to cookie
                const consentValue = stored === 'true';
                saveConsentToCookie(consentValue);
                return consentValue;
            }
        }
        return null;
    }
    catch (e) {
        warn('Failed to load consent from storage:', e);
        return null;
    }
}
/**
 * Save consent to cookie (primary) for cross-subdomain access
 * Also saves to localStorage as fallback
 */
function saveConsentToCookie(consent) {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        // Get the cookie domain (e.g., 'abc.com' -> '.abc.com')
        const cookieDomain = '.' + BrandclubStoreConfigUtils.getCookieDomain();
        // Save to cookie with cross-subdomain support
        cookies.set(CONSENT_COOKIE_NAME, String(consent), {
            domain: cookieDomain,
            maxAge: 365 * 24 * 60 * 60,
            path: '/',
            secure: true,
            sameSite: 'lax',
        });
        log(`Saved consent to cookie (${cookieDomain}):`, consent);
        // Also save to localStorage as fallback
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(CONSENT_STORAGE_KEY, String(consent));
        }
    }
    catch (e) {
        warn('Failed to save consent to cookie:', e);
        // Fallback to localStorage only
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(CONSENT_STORAGE_KEY, String(consent));
                log('Saved consent to localStorage (fallback):', consent);
            }
        }
        catch (fallbackError) {
            warn('Failed to save consent to localStorage fallback:', fallbackError);
        }
    }
}
/**
 * Read consent from dataLayer
 * Looks for consent state in window.dataLayer
 * Supports common consent management platform patterns
 */
function getConsentFromDataLayer() {
    var _a;
    if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
        return null;
    }
    try {
        // Look through dataLayer for consent events (most recent first)
        for (let i = window.dataLayer.length - 1; i >= 0; i--) {
            const item = window.dataLayer[i];
            // Pattern 1: {event: 'consent_update', analytics_consent: 'granted'/'denied'}
            if ((item === null || item === void 0 ? void 0 : item.event) === 'consent_update' && (item === null || item === void 0 ? void 0 : item.analytics_consent)) {
                return item.analytics_consent === 'granted';
            }
            // Pattern 2: {consent: {analytics: 'granted'/'denied'}}
            if ((_a = item === null || item === void 0 ? void 0 : item.consent) === null || _a === void 0 ? void 0 : _a.analytics) {
                return item.consent.analytics === 'granted';
            }
            // Pattern 3: {event: 'consent', ad_storage: 'granted'/'denied'}
            if ((item === null || item === void 0 ? void 0 : item.event) === 'consent' && (item === null || item === void 0 ? void 0 : item.ad_storage)) {
                return item.ad_storage === 'granted';
            }
            // Pattern 4: {event: 'consent', marketing: 'granted'/'denied'}
            if ((item === null || item === void 0 ? void 0 : item.event) === 'consent' && (item === null || item === void 0 ? void 0 : item.marketing)) {
                return item.marketing === 'granted';
            }
            // Pattern 5: OneTrust pattern {event: 'OneTrustGroupsUpdated'}
            if ((item === null || item === void 0 ? void 0 : item.event) === 'OneTrustGroupsUpdated' && typeof window !== 'undefined') {
                // Check if performance/targeting cookies are enabled
                const oneTrustActiveGroups = window.OnetrustActiveGroups;
                if (oneTrustActiveGroups) {
                    // Typically group C0004 is targeting/advertising
                    return oneTrustActiveGroups.includes('C0004') || oneTrustActiveGroups.includes('C0002');
                }
            }
        }
    }
    catch (e) {
        warn('Error reading consent from dataLayer:', e);
    }
    return null;
}
/**
 * Listen for consent updates via dataLayer push events
 */
function attachConsentListener() {
    if (consentListenerAttached || typeof window === 'undefined') {
        return;
    }
    try {
        // Ensure dataLayer exists
        window.dataLayer = window.dataLayer || [];
        const originalPush = window.dataLayer.push;
        // Intercept dataLayer.push to listen for consent updates
        window.dataLayer.push = function (...args) {
            // Call original push
            const result = originalPush.apply(window.dataLayer, args);
            // Check if this push contains consent data
            args.forEach((item) => {
                var _a;
                let consentGranted = null;
                if ((item === null || item === void 0 ? void 0 : item.event) === 'consent_update' && (item === null || item === void 0 ? void 0 : item.analytics_consent)) {
                    consentGranted = item.analytics_consent === 'granted';
                }
                else if ((_a = item === null || item === void 0 ? void 0 : item.consent) === null || _a === void 0 ? void 0 : _a.analytics) {
                    consentGranted = item.consent.analytics === 'granted';
                }
                else if ((item === null || item === void 0 ? void 0 : item.event) === 'consent' && (item === null || item === void 0 ? void 0 : item.ad_storage)) {
                    consentGranted = item.ad_storage === 'granted';
                }
                else if ((item === null || item === void 0 ? void 0 : item.event) === 'consent' && (item === null || item === void 0 ? void 0 : item.marketing)) {
                    consentGranted = item.marketing === 'granted';
                }
                else if ((item === null || item === void 0 ? void 0 : item.event) === 'OneTrustGroupsUpdated' && typeof window !== 'undefined') {
                    const oneTrustActiveGroups = window.OnetrustActiveGroups;
                    if (oneTrustActiveGroups) {
                        consentGranted = oneTrustActiveGroups.includes('C0004') || oneTrustActiveGroups.includes('C0002');
                    }
                }
                if (consentGranted !== null) {
                    log('Consent update detected from dataLayer:', consentGranted);
                    handleConsentChange(consentGranted);
                }
            });
            return result;
        };
        consentListenerAttached = true;
        log('Attached dataLayer consent listener');
    }
    catch (e) {
        warn('Failed to attach consent listener:', e);
    }
}
/**
 * Handle consent change and notify all registered providers
 */
function handleConsentChange(granted) {
    const previousConsent = userConsent;
    userConsent = granted;
    saveConsentToCookie(granted);
    // Only notify if consent actually changed
    if (previousConsent !== granted) {
        log('Consent changed from', previousConsent, 'to', granted);
        // Notify all registered tracking providers
        registeredProviders.forEach(provider => {
            try {
                if (granted && provider.grantConsent) {
                    provider.grantConsent();
                    log('Called grantConsent on provider');
                }
                else if (!granted && provider.revokeConsent) {
                    provider.revokeConsent();
                    log('Called revokeConsent on provider');
                }
            }
            catch (e) {
                warn('Error calling provider consent method:', e);
            }
        });
    }
}
/**
 * Register a tracking provider to receive consent updates
 */
function registerProvider(provider) {
    if (!registeredProviders.includes(provider)) {
        registeredProviders.push(provider);
        log('Registered tracking provider for consent updates');
        // Immediately call consent method with current state if known
        if (userConsent !== null) {
            try {
                if (userConsent && provider.grantConsent) {
                    provider.grantConsent();
                }
                else if (!userConsent && provider.revokeConsent) {
                    provider.revokeConsent();
                }
            }
            catch (e) {
                warn('Error calling provider consent method on registration:', e);
            }
        }
    }
}
/**
 * Initialize consent system - loads stored consent and sets up reactive listener
 */
function initializeConsent() {
    // Load consent from storage on first access
    if (userConsent === null) {
        // Check dataLayer first (most recent)
        const dataLayerConsent = getConsentFromDataLayer();
        if (dataLayerConsent !== null) {
            userConsent = dataLayerConsent;
        }
        else {
            // Check cookie/localStorage
            const storedConsent = loadConsentFromStorage();
            userConsent = storedConsent; // null if not found, false is default
        }
        log('Initialized consent:', userConsent);
    }
    // Attach reactive listener
    attachConsentListener();
}
/**
 * Check if user has granted consent for tracking
 * Initializes the consent system on first call
 */
function hasConsent() {
    initializeConsent();
    // Return current consent state, default to false if not set
    return userConsent === true;
}
/**
 * Manually set consent (for programmatic opt-in/opt-out)
 * Triggers reactive update to all providers
 */
function setConsent(granted) {
    log('Setting consent to:', granted);
    // Initialize if needed
    initializeConsent();
    // Update consent and notify providers
    handleConsentChange(granted);
    // Push to dataLayer so consent management platforms stay in sync
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
            event: 'consent_update',
            analytics_consent: granted ? 'granted' : 'denied',
            ad_storage: granted ? 'granted' : 'denied',
        });
    }
}
/**
 * Enable debug logging
 */
function enableDebug() {
    debug = true;
    log('Debug mode enabled for consent manager');
}

export { enableDebug, hasConsent, registerProvider, setConsent };
//# sourceMappingURL=consent.js.map
